<script lang="ts">
  import Icon from './Icon.svelte';
  import AppleLogo from './AppleLogo.svelte';
  import GoogleLogo from './GoogleLogo.svelte';
  import MapView from './MapView.svelte';
  import { photoUrl } from './api';
  import {
    buildGoogleMapsUrl,
    buildAppleMapsUrl,
    bestOrder,
  } from '~/shared/route-handoff.js';
  import type {
    PoiView,
    RouteStop,
    RouteMode,
    TripCityView,
  } from '~/shared/api.js';

  let {
    pois,
    filteredPois,
    cities,
    routeStops,
    onRouteStopsChange,
    routeMode,
    onRouteModeChange,
    onClearRouteRequested,
    activeCity = null,
  }: {
    pois: PoiView[];
    filteredPois: PoiView[];
    cities: TripCityView[];
    routeStops: RouteStop[];
    onRouteStopsChange: (next: RouteStop[]) => void;
    routeMode: RouteMode;
    onRouteModeChange: (mode: RouteMode) => void;
    onClearRouteRequested: () => void;
    /** Used by the embedded MapView for its initial center if the
     *  route has no stops yet. */
    activeCity?: TripCityView | null;
  } = $props();

  const poiById = $derived(new Map(pois.map((p) => [p.id, p])));
  function lookupPoi(id: number): PoiView | null {
    return poiById.get(id) ?? null;
  }

  // Stops that still reference live POIs — drops orphaned ids defensively.
  const livePoiIds = $derived(
    new Set(
      routeStops
        .filter((s): s is { kind: 'poi'; poiId: number } => s.kind === 'poi')
        .map((s) => s.poiId)
        .filter((id) => poiById.has(id)),
    ),
  );
  const hasHere = $derived(routeStops.length > 0 && routeStops[0]!.kind === 'here');
  const liveStops = $derived(
    routeStops.filter(
      (s) => s.kind === 'here' || (s.kind === 'poi' && poiById.has(s.poiId)),
    ),
  );

  const cityById = $derived(new Map(cities.map((c) => [c.id, c])));

  // Resolved positions for the MapView's route polyline + numbered
  // markers. We skip 'here' stops because we don't have the user's
  // live coordinates here — the map just shows the POI stops in
  // order. ('here' is still rendered in the route panel list.)
  const routeMapPoints = $derived(
    routeStops
      .filter((s): s is { kind: 'poi'; poiId: number } => s.kind === 'poi')
      .map((s) => {
        const p = poiById.get(s.poiId);
        return p ? { poiId: p.id, lat: p.lat, lng: p.lng } : null;
      })
      .filter((p): p is { poiId: number; lat: number; lng: number } => p !== null),
  );

  function poiCityLabel(poi: PoiView): string | null {
    if (poi.cityId !== null) {
      const city = cityById.get(poi.cityId);
      if (city) return city.name;
    }
    return poi.locality;
  }

  function togglePoi(id: number) {
    if (livePoiIds.has(id)) {
      onRouteStopsChange(routeStops.filter((s) => !(s.kind === 'poi' && s.poiId === id)));
    } else {
      onRouteStopsChange([...routeStops, { kind: 'poi', poiId: id }]);
    }
  }

  function addHere() {
    if (hasHere) return;
    onRouteStopsChange([{ kind: 'here' }, ...routeStops]);
  }

  function removeAt(idx: number) {
    onRouteStopsChange(routeStops.filter((_, i) => i !== idx));
  }

  function onBestOrderClick() {
    onRouteStopsChange(bestOrder(routeStops, lookupPoi));
  }

  const realStopCount = $derived(
    routeStops.filter((s) => s.kind === 'poi' && poiById.has(s.poiId)).length,
  );
  // Best order anchors at the first POI's coords; without that anchor
  // (i.e. when "here" is the first stop and we don't know its coords)
  // any ordering we compute is misleading. Hide rather than pretend.
  const showBestOrder = $derived(
    routeStops.length >= 3 && realStopCount >= 2 && !hasHere,
  );
  const showClearAll = $derived(routeStops.length > 0);
  const hasHandoff = $derived(realStopCount >= 1);
  /** When the route is just a single POI with no "Where I am", the
   *  handoff URLs become "locate" rather than "directions". The mode
   *  picker stops being meaningful in this case. */
  const isLocateOnly = $derived(realStopCount === 1 && !hasHere);
  const googleUrl = $derived(
    hasHandoff ? buildGoogleMapsUrl(routeStops, routeMode, lookupPoi) : null,
  );
  const appleUrl = $derived(
    hasHandoff ? buildAppleMapsUrl(routeStops, routeMode, lookupPoi) : null,
  );
  /** Apple Maps only supports multi-stop routing in driving mode; walk
   *  and transit cap at source + destination (= 2 points total). When
   *  the user has more than 2 points and is in walk/transit, the
   *  handoff would silently drop the waypoints — disable the button
   *  with an inline hint instead so the limitation is visible. */
  const totalRoutePoints = $derived(realStopCount + (hasHere ? 1 : 0));
  const appleMultiStopBlocked = $derived(
    hasHandoff &&
      !isLocateOnly &&
      (routeMode === 'walking' || routeMode === 'transit') &&
      totalRoutePoints > 2,
  );

  // ── Drag-to-reorder ────────────────────────────────────────────────
  // Live targetIdx so a drop-indicator can render between rows while
  // the user drags. Computed from the cursor's Y against each row's
  // bounding rect, ignoring the locked "here" row.
  type DragState = {
    fromIdx: number;
    startY: number;
    currentY: number;
    targetIdx: number;
    pointerId: number;
  };
  let drag: DragState | null = $state(null);
  let rowEls: (HTMLElement | null)[] = $state([]);

  function computeTargetIdx(clientY: number, _fromIdx: number): number {
    // Walk rows top-to-bottom; the first row whose midpoint is below
    // the cursor is the target slot. If we get past all rows, the
    // cursor is past the last row → drop at end (idx = length).
    for (let i = 0; i < rowEls.length; i++) {
      const el = rowEls[i];
      if (!el) continue;
      if (liveStops[i]?.kind === 'here') continue;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (clientY < mid) return i;
    }
    return liveStops.length;
  }

  function onHandleDown(e: PointerEvent, idx: number) {
    if (liveStops[idx]?.kind === 'here') return;
    // Whole row is draggable, but the × remove button needs to keep
    // its click — bail when pointerdown originates on it so the click
    // path runs unobstructed.
    const target = e.target as HTMLElement;
    if (target.closest('.stop-remove')) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag = {
      fromIdx: idx,
      startY: e.clientY,
      currentY: e.clientY,
      targetIdx: idx,
      pointerId: e.pointerId,
    };
  }

  function onHandleMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointerId) return;
    drag = {
      ...drag,
      currentY: e.clientY,
      targetIdx: computeTargetIdx(e.clientY, drag.fromIdx),
    };
  }

  function onHandleUp(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const { fromIdx, targetIdx } = drag;
    drag = null;

    if (targetIdx === fromIdx || targetIdx === fromIdx + 1) return;
    const reordered = [...routeStops];
    const moved = reordered.splice(fromIdx, 1)[0]!;
    // After removal, indices >= fromIdx shift down by 1. Compensate so
    // dropping at slot N really lands at slot N in the rendered list.
    const insertAt = targetIdx > fromIdx ? targetIdx - 1 : targetIdx;
    reordered.splice(insertAt, 0, moved);
    onRouteStopsChange(reordered);
  }

  function onHandleCancel() {
    drag = null;
  }

  function dragOffset(idx: number): string {
    if (!drag || drag.fromIdx !== idx) return '';
    const dy = drag.currentY - drag.startY;
    return `transform: translateY(${dy}px); z-index: 5;`;
  }

  function setRowEl(idx: number, el: HTMLElement | null) {
    rowEls[idx] = el;
  }

  // Trim stale element refs when stops shrink — otherwise rowEls keeps
  // pointing at removed DOM nodes and computeTargetIdx reads them.
  $effect(() => {
    if (rowEls.length > liveStops.length) {
      rowEls.length = liveStops.length;
    }
  });

  /** Drop-indicator visibility — true means render a line at this slot.
   *  Slot N means "drop will land before row N"; slot === length means
   *  "drop will land after the last row." We suppress indicators that
   *  correspond to a no-op move (dropping in the row's current slot or
   *  the slot immediately after it). */
  function showDropIndicatorAt(idx: number): boolean {
    if (!drag) return false;
    if (drag.targetIdx !== idx) return false;
    if (drag.targetIdx === drag.fromIdx) return false;
    if (drag.targetIdx === drag.fromIdx + 1) return false;
    return true;
  }
</script>

<div class="route-view">
  <!-- Mobile: route-panel on top, picker below (DOM order).
       Desktop: CSS `order` flips to picker | route panel | map. -->
  <!-- Route panel ─ what you're building -->
  <section class="route-panel">
    <header class="route-head">
      <div class="route-head-row">
        <h3 class="route-title">Your route</h3>
        <div class="route-head-actions">
          {#if showBestOrder}
            <button class="route-text-btn" type="button" onclick={onBestOrderClick}>
              <Icon name="sparkles" size={12} strokeWidth={2} />
              <span>Best order</span>
            </button>
          {/if}
          {#if showClearAll}
            <button class="route-text-btn route-text-btn--destructive" type="button" onclick={onClearRouteRequested}>
              <Icon name="trash" size={12} strokeWidth={2} />
              <span>Clear</span>
            </button>
          {/if}
        </div>
      </div>
      <!-- Mode picker lives in the header so it stays visible while
           the stop list scrolls. Hidden in locate-only mode (single
           POI, no "here") since the handoff opens the place page,
           not turn-by-turn directions. -->
      {#if !isLocateOnly}
        <div class="view-toggle mode-picker" role="radiogroup" aria-label="Travel mode">
          {#each [{ v: 'walking' as const, l: 'Walk' }, { v: 'transit' as const, l: 'Transit' }, { v: 'driving' as const, l: 'Drive' }] as opt (opt.v)}
            <button
              type="button"
              role="radio"
              class="view-toggle__seg {routeMode === opt.v ? 'view-toggle__seg--active' : ''}"
              aria-checked={routeMode === opt.v}
              onclick={() => onRouteModeChange(opt.v)}
            >{opt.l}</button>
          {/each}
        </div>
      {/if}
    </header>

    <div class="route-body">
    {#if liveStops.length === 0}
      <div class="empty">
        {#if pois.length === 0}
          <p class="empty-text">
            No stops yet. Bookmark some places first — your route's built from places saved to this trip.
          </p>
        {:else}
          <p class="empty-text">No stops yet. Tap places below to start your route.</p>
        {/if}
      </div>
    {:else}
      <ul class="stop-list">
        {#each liveStops as stop, idx (stop.kind === 'here' ? 'here' : stop.poiId)}
          {@const poi = stop.kind === 'poi' ? lookupPoi(stop.poiId) : null}
          {#if showDropIndicatorAt(idx)}
            <li class="drop-line" aria-hidden="true"></li>
          {/if}
          <li
            class="stop-row"
            class:stop-row--here={stop.kind === 'here'}
            class:stop-row--dragging={drag?.fromIdx === idx}
            data-route-row={idx}
            style={dragOffset(idx)}
            bind:this={() => rowEls[idx], (el) => setRowEl(idx, el)}
            onpointerdown={stop.kind === 'poi' ? (e) => onHandleDown(e, idx) : undefined}
            onpointermove={stop.kind === 'poi' ? onHandleMove : undefined}
            onpointerup={stop.kind === 'poi' ? onHandleUp : undefined}
            onpointercancel={stop.kind === 'poi' ? onHandleCancel : undefined}
          >
            {#if stop.kind === 'here'}
              <span class="here-icon" aria-hidden="true">
                <Icon name="map-pin" size={16} strokeWidth={2.25} />
              </span>
              <span class="stop-body">
                <span class="stop-name">Where I am</span>
                <span class="stop-meta">Starting point</span>
              </span>
            {:else if poi}
              <span class="drag-handle" aria-hidden="true">
                <Icon name="grip-vertical" size={16} strokeWidth={2} />
              </span>
              {#if poi.photoReference}
                <img class="stop-photo" src={photoUrl(poi.photoReference, 200) ?? ''} alt="" loading="lazy" />
              {:else}
                <span class="stop-photo stop-photo--blank" aria-hidden="true"></span>
              {/if}
              <span class="stop-body">
                <span class="stop-name">{poi.customName ?? poi.name}</span>
                {#if poiCityLabel(poi)}
                  <span class="stop-meta">{poiCityLabel(poi)}</span>
                {/if}
              </span>
            {/if}
            <button
              type="button"
              class="stop-remove"
              aria-label="Remove from route"
              onclick={() => removeAt(idx)}
            >
              <Icon name="x" size={14} strokeWidth={2.25} />
            </button>
          </li>
        {/each}
        {#if showDropIndicatorAt(liveStops.length)}
          <li class="drop-line" aria-hidden="true"></li>
        {/if}
      </ul>
    {/if}

    {#if !hasHere}
      <button class="add-here-btn" type="button" onclick={addHere}>
        <Icon name="plus" size={14} strokeWidth={2} />
        <span>Start from where I am</span>
      </button>
    {/if}

    {#if hasHandoff}
      <div class="handoff">
        {#if appleMultiStopBlocked}
          <button
            class="btn btn-primary handoff-btn"
            type="button"
            disabled
            title="Apple Maps only routes through multiple stops in driving mode. Switch to Drive, or use Google Maps."
          >
            <AppleLogo size={16} />
            <span>Send to Apple Maps <span class="handoff-hint">(max 2 stops)</span></span>
          </button>
        {:else}
          <a class="btn btn-primary handoff-btn" href={appleUrl ?? '#'} target="_blank" rel="noopener noreferrer">
            <AppleLogo size={16} />
            <span>Send to Apple Maps</span>
          </a>
        {/if}
        <a class="btn btn-primary handoff-btn" href={googleUrl ?? '#'} target="_blank" rel="noopener noreferrer">
          <GoogleLogo size={16} mono />
          <span>Send to Google Maps</span>
        </a>
      </div>
    {/if}
    </div>
  </section>

  <!-- Picker ─ what's available to add -->
  <section class="picker">
    <header class="picker-head">
      <h3 class="picker-title">Places</h3>
      {#if filteredPois.length > 0}
        <span class="picker-hint">Tap to add to your route</span>
      {/if}
    </header>

    <div class="picker-body">
    {#if pois.length === 0}
      <p class="picker-empty">
        Nothing bookmarked yet — head to the list view and add a few places before building a route.
      </p>
    {:else if filteredPois.length === 0}
      <p class="picker-empty">No places match the current filter.</p>
    {:else}
      <ul class="poi-grid">
        {#each filteredPois as poi (poi.id)}
          {@const checked = livePoiIds.has(poi.id)}
          <li>
            <button
              type="button"
              class="poi-card"
              class:poi-card--checked={checked}
              aria-pressed={checked}
              onclick={() => togglePoi(poi.id)}
            >
              <span class="poi-checkbox" aria-hidden="true">
                {#if checked}<Icon name="x" size={11} strokeWidth={2.5} />{/if}
              </span>
              {#if poi.photoReference}
                <img class="poi-photo" src={photoUrl(poi.photoReference, 360) ?? ''} alt="" loading="lazy" />
              {:else}
                <span class="poi-photo poi-photo--blank" aria-hidden="true"></span>
              {/if}
              <span class="poi-body">
                <span class="poi-name">{poi.customName ?? poi.name}</span>
                {#if poiCityLabel(poi)}
                  <span class="poi-meta">{poiCityLabel(poi)}</span>
                {/if}
              </span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
    </div>
  </section>

  <!-- Map ─ desktop only; renders the polyline + numbered stops. -->
  <div class="route-map-wrap">
    <MapView
      pois={[]}
      activeCity={activeCity}
      routeEmpty={true}
      routePoiIds={new Set()}
      routeStops={routeMapPoints}
      onPoiOpen={() => {}}
    />
  </div>
</div>

<style>
  .route-view {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  /* Mobile: DOM order rules — route-panel above, picker below.
     Desktop: switch to row, reorder to picker | route-panel | map. */
  .route-map-wrap { display: none; }
  @media (min-width: 768px) {
    .route-view {
      flex-direction: row;
      align-items: stretch;
      gap: 1rem;
      height: clamp(480px, 70vh, 760px);
    }
    .route-view .picker {
      order: 1;
      flex: none;
      width: 320px;
      align-self: stretch;
      /* Section is a flex column; .picker-head is fixed-height,
         .picker-body owns the scroll — same pattern as MapSidebar.
         Same border + radius as the sibling panels so it reads as a
         peer, not a floating list. */
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid var(--color-hairline);
      border-radius: var(--radius-md);
      background: var(--color-canvas);
    }
    .route-view .route-panel {
      order: 2;
      flex: none;
      width: 340px;
      align-self: stretch;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      /* The panel's own padding is moved to .route-body so the
         header sits flush with the panel edge and gets a clean
         border-bottom against the body. */
      padding: 0;
    }
    .route-map-wrap {
      order: 3;
      flex: 1;
      min-width: 0;
      display: block;
      align-self: stretch;
    }
    /* MapView's own .map-wrap caps height to clamp(420, 70vh, 720).
       Inside our flex-stretched stage, override to fill our height
       instead — same trick the map-stage uses on TripPage. */
    .route-map-wrap :global(.map-wrap) {
      height: 100% !important;
      width: 100%;
    }
    /* The route panel is only 340px wide in sidebar layout — too
       narrow for two side-by-side handoff buttons. Override the
       generic 480px viewport rule and keep them stacked. */
    .route-view .handoff { flex-direction: column; }
    .route-view .handoff-btn { flex: none; }

    /* Fixed-header pattern shared with MapSidebar: header is a
       flex: none sibling with a border-bottom; body owns the
       scroll. Padding moves from the section to the head/body so
       the divider runs edge-to-edge. */
    .route-view .picker-head,
    .route-view .route-head {
      padding: 14px 16px;
      border-bottom: 1px solid var(--color-hairline-soft);
    }
    .route-view .picker-body,
    .route-view .route-body {
      flex: 1;
      overflow-y: auto;
      padding: 12px 16px 16px;
      /* macOS overlay scrollbar sits in this padding zone instead
         of over the cards (see picker fix two commits ago). */
      scrollbar-gutter: stable;
    }
    /* Section gap of 0.75rem becomes redundant once head/body have
       their own padding + border-bottom. */
    .route-view .picker,
    .route-view .route-panel { gap: 0; }
  }

  /* ── Route panel ───────────────────────────────────────────────── */
  .route-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    /* Same border + radius as MapSidebar and the picker so the three
       panes read as peer panels. */
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-md);
    padding: 1rem;
    background: var(--color-canvas);
  }
  .route-head {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    flex: none;
  }
  .route-head-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .route-body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .route-title {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .route-head-actions {
    display: inline-flex;
    align-items: baseline;
    gap: 1rem;
  }
  .route-text-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: transparent;
    border: 0;
    color: var(--color-muted);
    font: inherit;
    font-size: 12px;
    cursor: pointer;
    padding: 0;
  }
  .route-text-btn:hover { color: var(--color-ink); }
  .route-text-btn--destructive:hover { color: var(--color-error); }

  .empty {
    padding: 1.5rem 1rem;
    text-align: center;
  }
  .empty-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-muted);
  }
  .drop-line {
    list-style: none;
    height: 3px;
    background: var(--color-ink);
    border-radius: 3px;
    margin: -1px 0;
  }

  .stop-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  .stop-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.375rem 0.5rem;
    border-radius: var(--radius-sm);
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline-soft);
    min-height: 48px;
    cursor: grab;
    /* Let pointer events drive drag without scroll fighting */
    touch-action: none;
  }
  .stop-row:active { cursor: grabbing; }
  .stop-row--here { cursor: default; }
  .stop-row--here {
    background: var(--color-surface-soft);
  }
  .stop-row--dragging {
    box-shadow: 0 8px 24px -8px rgba(0, 0, 0, 0.18);
    opacity: 0.95;
  }
  .here-icon {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-muted);
    flex-shrink: 0;
  }
  /* Visual cue that the row is draggable; pointer events live on the
     row itself. */
  .drag-handle {
    width: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-muted);
    flex-shrink: 0;
    pointer-events: none;
  }
  .stop-row:hover .drag-handle { color: var(--color-ink); }
  .stop-photo {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-sm);
    object-fit: cover;
    flex-shrink: 0;
  }
  .stop-photo--blank { background: var(--color-surface-strong, var(--color-surface-soft)); }
  .stop-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .stop-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .stop-meta {
    font-size: 12px;
    color: var(--color-muted);
  }
  .stop-remove {
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: transparent;
    border: 0;
    color: var(--color-muted);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .stop-remove:hover { background: var(--color-surface-soft); color: var(--color-ink); }

  .add-here-btn {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: transparent;
    color: var(--color-muted);
    border: 1px dashed var(--color-hairline);
    border-radius: 9999px;
    padding: 0.375rem 0.875rem;
    height: 32px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
  }
  .add-here-btn:hover { color: var(--color-ink); border-color: var(--color-border-strong); }

  /* Mode picker reuses the global .view-toggle pill styles so the
     Walk / Transit / Drive control reads as the same family of
     control as the List · Map · Auto · Route view-toggle. Centered
     so it reads as a route-level decision rather than a left-anchored
     filter chip. */
  .mode-picker { align-self: center; }

  .handoff {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  @media (min-width: 480px) {
    .handoff { flex-direction: row; }
    .handoff-btn { flex: 1; }
  }
  .handoff-btn {
    justify-content: center;
    text-decoration: none;
  }
  /* Disabled handoff (Apple walk/transit with >2 stops). The base .btn
     classes don't ship a global :disabled style, so without this the
     disabled button reads identical to the enabled siblings. */
  .handoff-btn:disabled {
    background: var(--color-surface-soft);
    color: var(--color-muted);
    border-color: var(--color-hairline);
    cursor: not-allowed;
  }
  .handoff-btn:disabled:hover {
    background: var(--color-surface-soft);
  }
  .handoff-hint {
    font-size: 12px;
    font-weight: 400;
    opacity: 0.85;
    margin-left: 0.375rem;
  }

  /* ── Picker ────────────────────────────────────────────────────── */
  .picker {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .picker-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    flex: none;
  }
  .picker-body {
    display: flex;
    flex-direction: column;
  }
  .picker-title {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .picker-hint {
    font-size: 12px;
    color: var(--color-muted);
  }
  .picker-empty {
    margin: 0;
    font-size: 13px;
    color: var(--color-muted);
  }
  /* Responsive grid — auto-fills so the picker scales from a single
     column on phones up to 4+ on wide desktops without per-breakpoint
     fiddling. */
  .poi-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.5rem;
  }
  .poi-grid > li { margin: 0; }
  .poi-card {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    background: var(--color-canvas);
    /* --color-hairline-soft (#ebebeb) on white was almost invisible —
       cards read as borderless. Step up to --color-hairline so the
       boxes have visible definition without being heavy. */
    border: 1px solid var(--color-hairline);
    border-radius: var(--radius-sm);
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    color: inherit;
    min-height: 56px;
    transition: border-color 100ms ease, background 100ms ease;
  }
  .poi-card:hover {
    background: var(--color-surface-soft);
    border-color: var(--color-border-strong);
  }
  .poi-card--checked {
    background: var(--color-surface-soft);
    border-color: var(--color-ink);
  }
  .poi-checkbox {
    width: 18px;
    height: 18px;
    border: 1.5px solid var(--color-hairline);
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: transparent;
    transition: background 100ms ease, border-color 100ms ease, color 100ms ease;
  }
  .poi-card--checked .poi-checkbox {
    background: var(--color-ink);
    border-color: var(--color-ink);
    color: var(--color-on-dark);
  }
  .poi-photo {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-sm);
    object-fit: cover;
    flex-shrink: 0;
  }
  .poi-photo--blank { background: var(--color-surface-strong, var(--color-surface-soft)); }
  .poi-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .poi-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .poi-meta {
    font-size: 12px;
    color: var(--color-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
